import React from 'react';
import { 
  ArrowLeft,
  Wrench,
  Warning,
  Question,
  CheckCircle,
  XCircle
} from '@phosphor-icons/react';
import { 
  Box,
  Typography,
  Container,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { PageContainer, Content } from '../../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #000000 0%,
    #1a1a1a 50%,
    #000000 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 6rem 2rem 3rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 24px;
  top: 24px;
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  padding-top: 2rem;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 1rem;
  border-radius: 8px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.5);
  
  &:before {
    display: none;
  }
`;

const Troubleshooting = () => {
  const navigate = useNavigate();

  const commonIssues = [
    {
      question: "App crashes on launch",
      solution: [
        "Force close the app and restart it",
        "Check for app updates in the App Store",
        "Clear app cache and data",
        "Reinstall the app if the issue persists"
      ]
    },
    {
      question: "Sync issues with cloud data",
      solution: [
        "Check your internet connection",
        "Ensure you're logged in to your account",
        "Try manually syncing from the settings menu",
        "Contact support if sync fails repeatedly"
      ]
    },
    {
      question: "Location services not working",
      solution: [
        "Enable Location Services in iOS Settings",
        "Grant location permissions to ServTrackr",
        "Check if GPS is enabled on your device",
        "Restart the app and your device if needed"
      ]
    },
    {
      question: "Notifications not appearing",
      solution: [
        "Check notification permissions in iOS Settings",
        "Enable notifications for ServTrackr",
        "Verify notification settings within the app",
        "Restart the app to refresh notification services"
      ]
    },
    {
      question: "Performance issues",
      solution: [
        "Close background apps",
        "Clear app cache",
        "Update to the latest version",
        "Check device storage space"
      ]
    }
  ];

  return (
    <PageContainer>
      <GradientHero>
        <BackButton onClick={() => navigate('/resources/help-center/ios')}>
          <ArrowLeft size={24} />
        </BackButton>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Wrench size={64} weight="duotone" style={{ marginBottom: '1rem' }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Troubleshooting Guide
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Solutions to common issues and problems with ServTrackr iOS
          </motion.p>
        </HeroContent>
      </GradientHero>

      <Content>
        <Container maxWidth="md">
          <Alert severity="info" sx={{ mb: 4 }}>
            Before contacting support, please try these troubleshooting steps. Most issues can be resolved by following these solutions.
          </Alert>

          {commonIssues.map((issue, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <StyledAccordion>
                <AccordionSummary
                  expandIcon={<Question size={24} weight="duotone" />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <Typography variant="h6">{issue.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {issue.solution.map((step, stepIndex) => (
                      <ListItem key={stepIndex}>
                        <ListItemIcon>
                          <CheckCircle size={24} weight="duotone" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </StyledAccordion>
            </motion.div>
          ))}

          <Alert severity="warning" sx={{ mt: 4 }}>
            If you're still experiencing issues after trying these solutions, please contact our support team for further assistance.
          </Alert>
        </Container>
      </Content>
    </PageContainer>
  );
};

export default Troubleshooting; 